import React from 'react'
import { observer } from 'mobx-react-lite'

import { useTheme } from '@mui/material/styles'

import { ANALYTICS_EVENTS, useEvent, useGlobalCopyStore, useScreenSize } from '../../common'
import { DateRange, MonthlyCategoryTotals, TrendsCopy } from '../../core'
import { LineChart } from '../../common/components/charts/LineChart'
import type { LineChartDataset, LineChartLabel } from '../../common/components/charts/LineChart'

import { TRENDS_ANALYTICS_METADATA } from '../constants/TrendsWidget'
import { getIncomeTotals, getSpendingTotals } from '../utils/SpendingData'

const getDatasets = (totals: MonthlyCategoryTotals[], dateRange: DateRange): LineChartDataset[] => {
  const incomeData = getIncomeTotals(totals, dateRange)
  const spendingData = getSpendingTotals(totals, dateRange)
  return [incomeData, spendingData].filter((data) => data.length > 0)
}

const getLabels = (datasets: LineChartDataset[], copy: TrendsCopy) => {
  const [dataA, dataB] = datasets
  const credits = dataA
  const debits = dataB ?? dataA
  const labels: LineChartLabel[] = []

  if (credits) {
    const totalIncome = credits.reduce((sum, item) => sum + item.y, 0)
    labels.push({ label: copy.income_label, amount: totalIncome })
  }

  if (debits) {
    const totalSpending = debits.reduce((sum, item) => sum + item.y, 0)
    labels.push({ label: copy.spending_label, amount: totalSpending })
  }

  return labels
}

interface TrendsChartTotalsProps {
  availableHeight?: number
  minHeight?: number
  selectedDateRange: DateRange
  totals: MonthlyCategoryTotals[]
}

const TrendsChartTotals: React.FC<TrendsChartTotalsProps> = ({
  availableHeight = 0,
  minHeight = 500,
  selectedDateRange,
  totals,
}) => {
  const theme = useTheme()
  const { onEvent } = useEvent()
  const { trends: trendsCopy } = useGlobalCopyStore()
  const { isSmallMobile, isMobile, isSmallTablet, isTablet } = useScreenSize()

  const colors = [theme.palette.chart?.chart1, theme.palette.chart?.chart2]
  const datasets = getDatasets(totals, selectedDateRange)
  const labels = getLabels(datasets, trendsCopy)

  const [chartDesktopHeight, setChartDesktopHeight] = React.useState<number>(0)
  const chartTabletHeight = 458
  const chartMobileHeight = 195
  const legendHeight = 56

  // prettier-ignore
  // eslint-disable-next-line no-nested-ternary
  const chartHeight = (isMobile || isSmallMobile) ? chartMobileHeight :
  (isSmallTablet || isTablet) ? chartTabletHeight : chartDesktopHeight;

  // Control responsive chart height based on available height
  React.useEffect(() => {
    const newChartHeight = Math.max(availableHeight - legendHeight, minHeight)
    setChartDesktopHeight(newChartHeight)
  }, [availableHeight])

  const handleItemClick = () => {
    onEvent(ANALYTICS_EVENTS.TRENDS_CLICK_CHART, TRENDS_ANALYTICS_METADATA)
  }

  return (
    <LineChart
      baseline="min"
      colors={colors}
      curveType="bump"
      datasets={datasets}
      height={chartHeight}
      labels={labels}
      onItemClick={handleItemClick}
      showArea={true}
      showAxisHighlight={true}
      showLegend={true}
      showTooltip={true}
      showXAxis={true}
      showXAxisTicks={true}
      showYAxis={true}
      showYAxisTicks={true}
      valueFormatterString="0.0a"
    />
  )
}

export default observer(TrendsChartTotals)
